import React, { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { Checkbox } from 'antd';
import { useCard, useLanguage } from '../../service';
import { useShallow } from 'zustand/react/shallow';
import {
    ImageCropper,
    ImageCropperRef,
} from '../../component';
import { getArtCanvasCoordinate } from '../../model';

export type SimplifiedImageInputRef = {
    isLoading: () => boolean;
};

export type SimplifiedImageInputProps = {
    receivingCanvas: HTMLCanvasElement | null;
    onCropChange?: (cropInfo: Partial<ReactCrop.Crop>, sourceType: 'offline' | 'online') => void;
    onTainted: () => void;
    onSourceLoaded: (crossorigin?: string) => void;
};

export const SimplifiedImageInput = forwardRef<SimplifiedImageInputRef, SimplifiedImageInputProps>(({
    receivingCanvas,
    onCropChange,
    onTainted,
    onSourceLoaded,
}, ref) => {
    const language = useLanguage();
    const imageCropperRef = useRef<ImageCropperRef>(null);
    const isUpdatingCrop = useRef(false);

    const {
        opacity,
        isPendulum,
        pendulumSize,
        art,
        artCrop,
        artData,
        artSource,
        artFit,
        artStyle,
        getUpdater,
        setCard,
    } = useCard(useShallow(({
        card: {
            opacity,
            isPendulum,
            pendulumSize,
            art,
            artCrop,
            artData,
            artSource,
            artFit,
            artStyle,
        },
        getUpdater,
        setCard,
    }) => ({
        opacity,
        isPendulum,
        pendulumSize,
        art,
        artCrop,
        artData,
        artSource,
        artFit,
        artStyle,
        getUpdater,
        setCard,
    })));

    const changeArtFit = useCallback((status: boolean) => {
        setCard(currentCard => ({
            ...currentCard,
            artFit: status,
        }));
    }, [setCard]);

    const changeArt = useCallback((source: string) => {
        getUpdater('art')(source);
    }, [getUpdater]);

    const changeArtData = useCallback((source: string) => {
        getUpdater('artData')(source);
    }, [getUpdater]);

    const changeArtSource = useCallback((type: 'offline' | 'online') => {
        getUpdater('artSource')(type);
    }, [getUpdater]);

    const changeImageCrop = useCallback((cropInfo: Partial<ReactCrop.Crop>, sourceType: 'offline' | 'online', byUser?: boolean) => {
        if (isUpdatingCrop.current) return;
        isUpdatingCrop.current = true;
        try {
            onCropChange?.(cropInfo, sourceType);
            if (cropInfo && byUser) {
                setCard(curr => ({
                    ...curr,
                    artCrop: cropInfo,
                }), true);
            }
        } finally {
            setTimeout(() => {
                isUpdatingCrop.current = false;
            }, 0);
        }
    }, [onCropChange, setCard]);

    // Wire up flip buttons — update artStyle.flipX / flipY in card state
    const handleImageStyleChange = useCallback((newStyle: { flipX: boolean; flipY: boolean }) => {
        setCard(curr => ({
            ...curr,
            artStyle: {
                ...curr.artStyle,
                flipX: newStyle.flipX,
                flipY: newStyle.flipY,
            },
        }));
    }, [setCard]);

    // Wire up force-fit button — sync with artFit card state
    const handleForceFitChange = useCallback((fit: boolean) => {
        changeArtFit(fit);
    }, [changeArtFit]);

    const ratio = getArtCanvasCoordinate(isPendulum, opacity, undefined, pendulumSize).ratio;

    useImperativeHandle(ref, () => ({
        isLoading: () => imageCropperRef.current?.isLoading() ?? false,
    }));

    return (
        <div className="simplified-image-input">
            {/* Auto-frame Checkbox */}
            <div style={{ marginBottom: '16px' }}>
                <Checkbox
                    checked={artFit}
                    onChange={(e) => changeArtFit(e.target.checked)}
                >
                    {language['input.art-fit.label'] || 'Auto-fit'}
                </Checkbox>
            </div>

            {/* Image Cropper — all buttons now wired up */}
            <ImageCropper
                ref={imageCropperRef}
                title={language['input.card-art.label'] || 'Card Artwork'}
                defaultSourceType={artSource}
                defaultExternalSource={art}
                defaultInternalSource={artData}
                defaultCropInfo={artCrop}
                forceFit={artFit}
                imageStyle={artStyle}
                receivingCanvas={receivingCanvas}
                onSourceChange={(type, data) => {
                    changeArtSource(type);
                    if (type === 'offline') changeArtData(data);
                    else changeArt(data);
                }}
                onCropChange={changeImageCrop}
                onTainted={onTainted}
                onSourceLoaded={onSourceLoaded}
                onForceFitChange={handleForceFitChange}
                onImageStyleChange={handleImageStyleChange}
                onMaxSizeExceeded={() => {}}
                ratio={ratio}
            />
        </div>
    );
});

// @ts-ignore
SimplifiedImageInput.displayName = 'SimplifiedImageInput';
